import { useCallback, useState } from 'react'
import { AxiosError } from 'axios'

import { axiosInstance } from '../helpers'

interface PlaybackUrlResponse {
    url: string
    expires_in: number
}

interface ErrorEnvelope {
    error?: string
}

interface UseCompletionVideoPlaybackResult {
    url: string | null
    loading: boolean
    error: string | null
    fetchUrl: (videoId: number) => Promise<string | null>
    reset: () => void
}

/**
 * Fetches a short-lived (1 hour) presigned S3 playback URL for a
 * WorkOrderCompletionVideo. The URL must not be cached beyond its
 * expires_in window, so callers should invoke `fetchUrl` on demand
 * (e.g. at the moment a reviewer opens the video modal) and call
 * `reset` when the modal closes.
 */
export const useCompletionVideoPlayback = (): UseCompletionVideoPlaybackResult => {
    const [url, setUrl] = useState<string | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const fetchUrl = useCallback(async (videoId: number) => {
        setLoading(true)
        setError(null)
        try {
            const res = await axiosInstance.get<PlaybackUrlResponse>(
                `/workorder/completion-video/${videoId}/playback_url/`,
            )
            setUrl(res.data.url)
            return res.data.url
        } catch (e) {
            const axiosErr = e as AxiosError<ErrorEnvelope>
            const serverMsg = axiosErr?.response?.data?.error
            const status = axiosErr?.response?.status
            const msg =
                status === 404
                    ? "Video isn't ready yet. Please try again in a moment."
                    : serverMsg ?? 'Unable to load video. Please try again.'
            setError(msg)
            setUrl(null)
            return null
        } finally {
            setLoading(false)
        }
    }, [])

    const reset = useCallback(() => {
        setUrl(null)
        setError(null)
        setLoading(false)
    }, [])

    return { url, loading, error, fetchUrl, reset }
}
